import {Box, ButtonBase, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import {SxProps, Theme} from "@mui/material/styles";
import {Datepicker} from "@mobiscroll/react";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import "@mobiscroll/react/dist/css/mobiscroll.scss";
import {addDays, formatFullDMY, isToday, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {
    StepButtonsPlacement,
    insideStepButtonSx,
    mergeSx,
    stepButtonSx,
    toPickerDate,
    triggerStateSx,
    useCalendarLocale,
    useMobiscrollThemeStyles,
    useStepButtonsInside,
    weekdayLabel,
    WEEKDAY_SLOT,
    DATE_SLOT,
    DatePickerSize,
    DatePickerChangeReason,
    DATE_PICKER_SIZE_TOKENS,
    NEW_DATE_PICKER_POPUP_CLASS,
} from "./datePickerShared";

/**
 * Minimal single-date picker: our own trigger (date label + optional step / today buttons)
 * over a headless Mobiscroll calendar popup. There is deliberately no range mode — see
 * {@link RangeDatePicker} — so this component stays small and independently tweakable.
 */
export interface SingleDatePickerProps {
    value?: Date;
    onChange?: (date: Date, reason: DatePickerChangeReason) => void;
    disabled?: boolean;
    showWeekday?: boolean;
    showStepButtons?: boolean;
    showTodayButton?: boolean;
    stepButtonsPlacement?: StepButtonsPlacement;
    /** "medium" (40px, default) or "small" (36px). Matches MUI input sizing. */
    size?: DatePickerSize;
    /** Optional fixed trigger width (px). Sizes to content otherwise. */
    width?: number;
    sx?: SxProps<Theme>;
}

type PickerChangeEvent = {value?: unknown};

export function SingleDatePicker(props: SingleDatePickerProps) {
    const {t} = useTranslation();
    const {language, calendarLocale} = useCalendarLocale();
    const accent = useMobiscrollThemeStyles();

    const pickerRef = useRef<Datepicker>(null);
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

    const showWeekday = props.showWeekday ?? true;
    const showStepButtons = props.showStepButtons ?? true;
    const showTodayButton = props.showTodayButton ?? true;
    const disabled = props.disabled ?? false;
    const stepInside = useStepButtonsInside(props.stepButtonsPlacement ?? "auto", showStepButtons);

    const size = props.size ?? "medium";
    const sz = DATE_PICKER_SIZE_TOKENS[size];

    // Fall back to today so the picker is always controlled even without a value.
    const today = startOfDay(new Date());
    const value = props.value ? startOfDay(props.value) : today;
    const todaySelected = isToday(value);

    const hasFixedWidth = props.width != null;
    const triggerWidth: number | string = hasFixedWidth ? (props.width as number) : "max-content";

    const renderDateLabel = (date: Date) => {
        // Fixed-width, left-aligned slot so proportional digits (League Spartan's "1" is
        // ~half-width) can't reflow the max-content trigger as the date changes. See DATE_SLOT.
        const dateBox = (
            <Box
                component="span"
                sx={{
                    display: "inline-block",
                    width: DATE_SLOT,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    textAlign: "left",
                }}
            >
                {formatFullDMY(date)}
            </Box>
        );

        if (!showWeekday) {
            return dateBox;
        }

        return (
            <Box component="span" sx={{display: "inline-flex", alignItems: "center", minWidth: 0}}>
                <Box
                    component="span"
                    sx={{
                        flex: `0 0 ${WEEKDAY_SLOT}`,
                        width: WEEKDAY_SLOT,
                        mr: 0.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                    }}
                >
                    {weekdayLabel(language, date)}
                </Box>

                {dateBox}
            </Box>
        );
    };

    const openPicker = () => {
        if (!disabled) {
            pickerRef.current?.open();
        }
    };

    const navigateByDays = (days: number) => {
        if (disabled) {
            return;
        }
        props.onChange?.(startOfDay(addDays(value, days)), "step");
    };

    const goToToday = () => {
        if (disabled) {
            return;
        }
        props.onChange?.(today, "today");
    };

    const handlePickerChange = ({value: raw}: PickerChangeEvent) => {
        const date = toPickerDate(Array.isArray(raw) ? raw[0] : raw);
        if (date) {
            props.onChange?.(date, "selection");
        }
    };

    const stepButton = (direction: -1 | 1) => (
        <IconButton
            aria-label={direction === -1 ? t("Previous day") : t("Next day")}
            disabled={disabled}
            onClick={() => navigateByDays(direction)}
            sx={stepInside ? insideStepButtonSx(size) : stepButtonSx(size)}
        >
            {direction === -1 ? <ChevronLeftIcon sx={{fontSize: sz.stepIcon}}/> : <ChevronRightIcon sx={{fontSize: sz.stepIcon}}/>}
        </IconButton>
    );

    return (
        <Box sx={[{display: "inline-flex", alignItems: "center"}, ...mergeSx(props.sx)]}>
            <Stack direction="row" alignItems="center" spacing={stepInside ? 0 : 1}>
                {showStepButtons && !stepInside && stepButton(-1)}

                <Box
                    ref={setAnchorElement}
                    aria-disabled={disabled ? "true" : undefined}
                    sx={{
                        width: triggerWidth,
                        maxWidth: "100%",
                        height: sz.height,
                        display: "flex",
                        alignItems: "center",
                        boxSizing: "border-box",
                        borderRadius: {xs: 1, md: 2},
                        border: "1px solid",
                        borderColor: "rgba(0, 0, 0, 0.23)",
                        backgroundColor: "background.paper",
                        boxShadow: "none",
                        overflow: "hidden",
                        ...triggerStateSx(disabled, accent),
                    }}
                >
                    {showStepButtons && stepInside && stepButton(-1)}

                    {showTodayButton && (
                        <Tooltip title={t("Set to today")} arrow>
                            <span>
                                <IconButton
                                    aria-label={t("Set to today")}
                                    disabled={disabled}
                                    size="small"
                                    onClick={event => {
                                        event.stopPropagation();
                                        goToToday();
                                    }}
                                    sx={{
                                        width: sz.todayButton,
                                        height: sz.todayButton,
                                        mx: 0.25,
                                        borderRadius: 1,
                                        color: todaySelected ? "secondary.main" : "text.secondary",
                                        "&:hover": {
                                            backgroundColor: "action.hover",
                                            color: todaySelected ? "secondary.main" : "text.primary",
                                        },
                                    }}
                                >
                                    {todaySelected
                                        ? <CalendarMonthIcon sx={{fontSize: sz.todayIcon}}/>
                                        : <EventRepeatOutlinedIcon sx={{fontSize: sz.todayIcon}}/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    <ButtonBase
                        disabled={disabled}
                        onClick={openPicker}
                        aria-label={t("Select date")}
                        sx={{
                            flex: hasFixedWidth ? 1 : "0 0 auto",
                            alignSelf: "stretch",
                            minWidth: 0,
                            justifyContent: "flex-start",
                            pl: showTodayButton ? 1 : stepInside ? 0.75 : 1.5,
                            pr: stepInside ? 0.75 : 1.5,
                            color: disabled ? "text.disabled" : "text.primary",
                            textAlign: "left",
                            cursor: disabled ? "not-allowed" : "pointer",
                        }}
                    >
                        <Typography
                            noWrap
                            sx={{
                                ...(hasFixedWidth ? {width: "100%"} : {}),
                                minWidth: 0,
                                fontSize: sz.fontSize,
                                fontWeight: 500,
                                lineHeight: sz.lineHeight,
                                mt: sz.textNudge,
                                textAlign: "left",
                                fontVariantNumeric: "tabular-nums",
                            }}
                        >
                            {renderDateLabel(value)}
                        </Typography>
                    </ButtonBase>

                    {showStepButtons && stepInside && stepButton(1)}
                </Box>

                {showStepButtons && !stepInside && stepButton(1)}
            </Stack>

            <Datepicker
                ref={pickerRef}
                disabled={disabled}
                value={value}
                onChange={handlePickerChange}
                locale={calendarLocale}
                cssClass={NEW_DATE_PICKER_POPUP_CLASS}
                theme="material"
                themeVariant="light"
                dateFormat={showWeekday ? "DDD DD.MM.YYYY" : "DD.MM.YYYY"}
                controls={["calendar"]}
                firstDay={1}
                select="date"
                showInput={false}
                showOnClick={false}
                showOnFocus={false}
                anchor={anchorElement || undefined}
                responsive={{
                    xsmall: {controls: ["calendar"], display: "bottom", touchUi: true},
                    small: {controls: ["calendar"], display: "anchored", touchUi: false},
                    custom: {breakpoint: 800, controls: ["calendar"], display: "anchored", touchUi: false},
                }}
            />
        </Box>
    );
}

export default SingleDatePicker;
